import React from 'react';
import Modal from './ui/Modal';
import { CopyrightInfo } from '../types/models';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AttributionIcon from '@mui/icons-material/Attribution';

interface CopyrightInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  copyrightInfo: CopyrightInfo;
  songTitle: string;
  artistName: string;
}

export function CopyrightInfoModal({ 
  isOpen, 
  onClose, 
  copyrightInfo, 
  songTitle, 
  artistName 
}: CopyrightInfoModalProps) {
  const getPermissionLevel = () => {
    if (copyrightInfo.freeForUse) {
      return {
        level: 'Free For Use',
        description: 'This song can be used freely without any restrictions.',
        color: 'text-cyan-accent',
        bgColor: 'bg-cyan-accent/10',
        icon: <CheckCircleIcon className="text-cyan-accent" />
      };
    }
    
    if (copyrightInfo.doNotUse) {
      return {
        level: 'Do Not Use',
        description: 'This song cannot be used in any way without explicit permission.',
        color: 'text-naia-pink',
        bgColor: 'bg-naia-pink/10',
        icon: <CancelIcon className="text-naia-pink" />
      };
    }
    
    const restrictions = [];
    if (copyrightInfo.attributionRequired) {
      restrictions.push('Attribution Required');
    }
    if (copyrightInfo.doNotMonetize) {
      restrictions.push('Do Not Monetize');
    }
    
    if (restrictions.length > 0) {
      return {
        level: restrictions.join(' & '),
        description: 'This song has specific usage restrictions. Please review the details below.',
        color: 'text-soft-violet',
        bgColor: 'bg-soft-violet/10',
        icon: <InfoIcon className="text-soft-violet" />
      };
    }
    
    return {
      level: 'Usage Rights Not Specified',
      description: 'Copyright usage permissions are not specified for this song.',
      color: 'text-slate-grey',
      bgColor: 'bg-slate-grey/10',
      icon: <InfoIcon className="text-slate-grey" />
    };
  };

  const permissionLevel = getPermissionLevel();

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Copyright Info">
      <div className="space-y-4">
        <div className="flex items-center gap-3 mb-4">
          <div className={`p-2 rounded-full ${permissionLevel.bgColor}`}>
            {permissionLevel.icon}
          </div>
          <div>
            <p className="text-slate-grey text-sm">{songTitle} by {artistName}</p>
          </div>
        </div>

        <div className={`p-4 rounded-xl ${permissionLevel.bgColor} mb-6`}>
          <h3 className={`font-semibold ${permissionLevel.color} mb-2`}>
            {permissionLevel.level}
          </h3>
          <p className="text-slate-grey text-sm">
            {permissionLevel.description}
          </p>
        </div>

        <div className="space-y-4 mb-6">
          <h4 className="font-semibold text-pearl-white mb-3">Usage Permissions:</h4>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              {copyrightInfo.freeForUse ? (
                <CheckCircleIcon className="text-cyan-accent text-lg" />
              ) : (
                <CancelIcon className="text-naia-pink text-lg" />
              )}
              <span className="text-sm text-slate-grey">Free For Use</span>
            </div>

            <div className="flex items-center gap-2">
              {copyrightInfo.attributionRequired ? (
                <AttributionIcon className="text-soft-violet text-lg" />
              ) : (
                <CheckCircleIcon className="text-cyan-accent text-lg" />
              )}
              <span className="text-sm text-slate-grey">
                {copyrightInfo.attributionRequired ? 'Attribution Required' : 'No Attribution Required'}
              </span>
            </div>

            <div className="flex items-center gap-2">
              {copyrightInfo.doNotMonetize ? (
                <CancelIcon className="text-naia-pink text-lg" />
              ) : (
                <AttachMoneyIcon className="text-cyan-accent text-lg" />
              )}
              <span className="text-sm text-slate-grey">
                {copyrightInfo.doNotMonetize ? 'Do Not Monetize' : 'Monetization Allowed'}
              </span>
            </div>

            <div className="flex items-center gap-2">
              {copyrightInfo.doNotUse ? (
                <CancelIcon className="text-naia-pink text-lg" />
              ) : (
                <CheckCircleIcon className="text-cyan-accent text-lg" />
              )}
              <span className="text-sm text-slate-grey">
                {copyrightInfo.doNotUse ? 'Do Not Use' : 'Usage Allowed'}
              </span>
            </div>
          </div>
        </div>

        {copyrightInfo.customText && (
          <div className="p-4 bg-white/5 rounded-xl mb-6">
            <h4 className="font-semibold text-pearl-white mb-2">Custom Attribution Text:</h4>
            <p className="text-sm text-slate-grey leading-relaxed">
              {copyrightInfo.customText}
            </p>
          </div>
        )}

      </div>
    </Modal>
  );
}